/**
 * Assembly for server-side pose. The one place the tested pieces meet the app.
 *
 * Everything with a decision in it lives elsewhere and is unit-tested —
 * `poseSourceRouter` (what), `PoseSourceController` (when),
 * `poseRemoteConfig` (whether), `RemotePoseClient` (how), `EndpointProbe`
 * (reachable). This module is deliberately thin wiring plus the two IO
 * boundaries that cannot be unit-tested in jsdom: the canvas encoder and the
 * Firestore read.
 *
 * ── Inert until every one of these is true ───────────────────────────────
 *
 *   1. `REACT_APP_POSE_ENDPOINT` is set — and, in a prod build, is not the dev
 *      service (§7.2; `readEndpoint` below);
 *   2. the Firestore config document says `serverPoseEnabled: true`;
 *   3. the router picks `server` for this attempt.
 *
 * Any of them missing and `PoseDetectionManager` takes the worker path it has
 * always taken, in the same call, in the same tick.
 */

import { logWarning } from "@/Utils/logger";
import {
  RemotePoseClient,
  SEND_WIDTH,
  SEND_HEIGHT,
  type RemotePoseStats,
} from "./RemotePoseClient";
import {
  DEFAULT_POSE_REMOTE_CONFIG,
  loadPoseRemoteConfig,
  type PoseRemoteConfig,
} from "./poseRemoteConfig";
import { PoseSourceController } from "./PoseSourceController";
import { EndpointProbe, PROBE_TIMEOUT_MS } from "./EndpointProbe";
import { getDeviceClass, getDeviceMetrics } from "./deviceCapability";
import {
  blindWhileVisible,
  detectorHealth,
  jointUnseenWhileFramed,
  phoneTooSlow,
} from "./liveHealth";
import { hasModelInitFailed } from "./modelInitState";
import { getPoseSourceOverride } from "./poseSourceOverride";
import type { Pose } from "@tensorflow-models/pose-detection";

const JPEG_QUALITY = 0.6;

/**
 * The pose service this build may call — and a prod build may never call the
 * dev one.
 *
 * Until 2.5.21 a prod build refused every endpoint, which kept server pose dark
 * in prod by construction. Prod now uses `pose-inference` in `truetoform-6685b`,
 * so the guard narrows to the leak it always existed for. `TRAPS.md` requires
 * every `REACT_APP_*` key in `.env.local` to be set in every prod env file,
 * because react-scripts loads `.env.local` for production builds too — but
 * `.env.prod`, `.env.malchus`, `.env.neems`, `.env.sisley` and
 * `.env.stylematch` are **gitignored** (`.gitignore:16-25`) while `.env.local`
 * is tracked. A prod file missing the key therefore inherits the DEV URL, which
 * is exactly how `REACT_APP_REDEEM_ENDPOINT` shipped prod tenants pointing at
 * the dev API in August. `scripts/set-build-time.js` fails that build, but only
 * on a machine that has the prod files; this check is the half that is in git.
 *
 * Both of the dev service's URL forms are matched: Cloud Run answers on the
 * project-number host and on the hashed one alike.
 */
const DEV_POSE_SERVICE = /437403722141|tdmvwpqp3q|truetoform-dev/;

function readEndpoint(): string {
  const raw = process.env.REACT_APP_POSE_ENDPOINT;
  if (!raw) return "";
  if (process.env.REACT_APP_IS_PROD_ENV === "true" && DEV_POSE_SERVICE.test(raw)) {
    return "";
  }
  return raw.replace(/\/+$/, "");
}

/* ─────────────────────────── the canvas boundary ────────────────────────── */

let srcCanvas: HTMLCanvasElement | null = null;
let dstCanvas: HTMLCanvasElement | null = null;

const JPEG_DATA_URL_PREFIX = "data:image/jpeg;base64,";

/**
 * A JPEG data URL → the JPEG itself, or `null` for anything else.
 *
 * `toDataURL` quietly falls back to PNG for a type the browser cannot encode,
 * and the server refuses anything but a JPEG (`400 BAD_FRAME`), so a PNG is
 * dropped here rather than spending a round trip to be refused.
 */
function jpegDataUrlToBlob(dataUrl: string): Blob | null {
  if (dataUrl.indexOf(JPEG_DATA_URL_PREFIX) !== 0) return null;
  const binary = atob(dataUrl.slice(JPEG_DATA_URL_PREFIX.length));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "image/jpeg" });
}

/**
 * `ImageData` → a 288×384 JPEG.
 *
 * Two canvases, not one: `putImageData` ignores the transform matrix, so an
 * `ImageData` cannot be scaled on the way in. It goes onto the first at native
 * size, then `drawImage` scales that canvas onto the second.
 *
 * **Main-thread canvases only — never `frameWorker.ts`.** That worker needs
 * `OffscreenCanvas`, which Safari does not provide inside workers (`TRAPS.md`),
 * and iOS Safari is 62.5% of BUG-0004's affected sessions — the population this
 * feature recovers outright. Encoding there would exclude exactly the users it
 * is for.
 *
 * **`toDataURL`, never `toBlob`.** Chromium runs a main-thread `toBlob` JPEG
 * encode as an IDLE task and only forces it after `kIdleTaskStartTimeoutDelayMs`
 * — 4,000 ms on Android (`canvas_async_blob_creator.cc`). A scan screen gives
 * Chrome few idle periods, so on prod 2.5.21 the cloud frames from both Android
 * phones that used it went out mostly on a 4.35–4.75 s tick against an 89 ms
 * server, and every rotation failed. Dev builds had the same wait, but only in
 * some runs: on 2026-09-16, 18:17–18:25 UTC, both Android test phones sent dev
 * frames ~4.2–4.5 s apart, while most dev runs sent one every 0.25–0.6 s. Why
 * dev mostly escaped is not proven. The likeliest reason is the dev pose pill:
 * its frame-rate readout keeps changing while frames are fast, and Blink starts
 * an idle period after every frame the page draws. `toDataURL` encodes
 * synchronously — a few milliseconds for a 288×384 frame — so whether the page
 * redraws no longer matters.
 */
function encodeFrame(image: ImageData): Promise<Blob | null> {
  try {
    if (!srcCanvas) srcCanvas = document.createElement("canvas");
    if (!dstCanvas) dstCanvas = document.createElement("canvas");

    if (srcCanvas.width !== image.width) srcCanvas.width = image.width;
    if (srcCanvas.height !== image.height) srcCanvas.height = image.height;
    if (dstCanvas.width !== SEND_WIDTH) dstCanvas.width = SEND_WIDTH;
    if (dstCanvas.height !== SEND_HEIGHT) dstCanvas.height = SEND_HEIGHT;

    const sctx = srcCanvas.getContext("2d");
    const dctx = dstCanvas.getContext("2d");
    if (!sctx || !dctx) return Promise.resolve(null);

    sctx.putImageData(image, 0, 0);
    dctx.drawImage(srcCanvas, 0, 0, SEND_WIDTH, SEND_HEIGHT);

    return Promise.resolve(
      jpegDataUrlToBlob(dstCanvas.toDataURL("image/jpeg", JPEG_QUALITY))
    );
  } catch (e) {
    return Promise.resolve(null);
  }
}

/* ───────────────────────────── the service ──────────────────────────────── */

/** TEMPORARY, dev only: the raw inputs for the scan screen's pose source pill. */
export interface DevPoseStatus {
  source: "device" | "server";
  reason: string;
  pinned: boolean;
  /** `null` when the cloud path can run. */
  cloudUnavailableReason: string | null;
  serverPoseRequests: number;
  serverPoseFailures: number;
  detectorBroken: boolean;
}

class ServerPoseService {
  private config: PoseRemoteConfig = DEFAULT_POSE_REMOTE_CONFIG;
  private probe: EndpointProbe | null = null;
  private client: RemotePoseClient | null = null;
  private controller: PoseSourceController | null = null;
  private initStarted = false;
  /** The config read has settled, enabled or not. Only the dev pill reads it. */
  private configLoaded = false;

  private endpoint = readEndpoint();

  /**
   * Read the config and probe the endpoint. Never throws, never blocks a scan:
   * a one-shot Firestore `getDoc` has no cache and no retry and fails
   * intermittently (`HERE_BE_DRAGONS.md` § "Firestore reads"), so every failure
   * here leaves the device path in force.
   */
  async initialize(): Promise<void> {
    if (this.initStarted || !this.endpoint) return;
    this.initStarted = true;

    this.config = await loadPoseRemoteConfig({
      readDoc: async () => {
        // Imported HERE rather than at module scope, and not for tidiness.
        //
        // A static `import { db } from "@/Utils/firebase"` puts `firebase/auth`
        // in `PoseDetectionManager`'s transitive graph by a path that no
        // existing test mock cuts. Jest resolves it to the Node build, which
        // pulls in `undici`, which touches `TextEncoder` at module scope — and
        // Jest 27's jsdom does not provide it. The whole detection suite then
        // fails to LOAD, with the error pointing at `src/firebase.js:3`, a file
        // none of those tests mention. See TRAPS.md § "firebase/auth@10.x +
        // undici". Deferring the import keeps that out of the static graph,
        // and as a side effect keeps Firestore out of the initial bundle for
        // the users who never take this path.
        const [{ db }, { doc, getDoc }] = await Promise.all([
          import("@/Utils/firebase"),
          import("firebase/firestore"),
        ]);
        const snap = await getDoc(doc(db, "remote_config", "pwa"));
        return snap.exists() ? snap.data() : null;
      },
    });
    this.configLoaded = true;

    if (!this.config.enabled) return;

    this.client = new RemotePoseClient({
      endpoint: this.endpoint,
      fetchImpl: (...args) => fetch(...args),
      encodeFrame,
      getIdToken: async () => {
        try {
          // Deferred for the same reason as the Firestore import above.
          const { ensureAuthedUser } = await import("@/Utils/ensureAuthedUser");
          const user = await ensureAuthedUser();
          return await user.getIdToken();
        } catch (e) {
          return null;
        }
      },
      now: () => performance.now(),
    });

    // `GET /status` before the camera is used, and again at every ALIGNMENT
    // entry (`resetAttempt`). This IS the routing gate for the device-class and
    // live-degraded triggers — but deliberately not for the models-failed one,
    // which has nothing to fall back to.
    this.probe = new EndpointProbe({
      url: this.endpoint + "/status",
      fetchImpl: (...args) => fetch(...args),
      timeoutMs: PROBE_TIMEOUT_MS,
      onFailure: () => {
        logWarning("[serverPose] endpoint probe failed", {
          endpoint: this.endpoint,
        });
      },
    });

    this.controller = new PoseSourceController({
      getConfig: () => this.config,
      getDeviceClass,
      hasModelFailed: hasModelInitFailed,
      isNetworkDegraded: () =>
        !!this.client && this.client.isDegraded(this.config.maxRttMs),
      // The phone's own frame rate under 8 Hz this attempt (liveHealth.ts). A
      // boot benchmark cannot see thermal throttling, a WebGL context lost
      // mid-session, competing tabs or low-power mode; this can. Judged on
      // frames the PHONE detected only: the cloud's own 4-10 fps used to read
      // as "phone too slow" on every cloud attempt.
      isDeviceDegraded: () => phoneTooSlow.isSlow(),
      isOnline: () =>
        typeof navigator === "undefined" || navigator.onLine !== false,
      getProbe: () => (this.probe ? this.probe.getState() : "pending"),
      getBenchmarkVerdict: () => {
        const benchmark = getDeviceMetrics().benchmark;
        return benchmark ? benchmark.verdict : "unknown";
      },
      isDetectorBroken: () => detectorHealth.isBroken(),
      isBlindWhileVisible: () => blindWhileVisible.isBlind(),
      getJointUnseenSource: () => jointUnseenWhileFramed.unseenSource(),
      // TEMPORARY, dev only: the scan screen pill. Always "auto" in prod builds.
      getOverride: getPoseSourceOverride,
    });

    await this.probe.refresh();
  }

  /** True when this frame should go to the server rather than the worker. */
  shouldUseServer(): boolean {
    if (!this.client || !this.controller) return false;
    return this.controller.current().source === "server";
  }

  detect(image: ImageData, opts?: { tick?: number }): Promise<Pose[]> {
    if (!this.client) return Promise.resolve([]);
    return this.client.detect(image, opts);
  }

  /** Pin the source for the rotation. Called when the countdown starts. */
  freezeForRotation(): void {
    if (this.controller) this.controller.freeze();
  }

  /**
   * Called at each ALIGNMENT entry, alongside the other per-attempt resets.
   *
   * Also re-asks whether the server is reachable. It used to be asked once per
   * page load, so a server that went away after boot was chosen again on every
   * retry. The previous answer stands until the new one lands (EndpointProbe),
   * so a healthy session does not flicker to the device while it checks.
   */
  resetAttempt(): void {
    if (this.controller) this.controller.resetAttempt();
    if (this.client) this.client.resetStats();
    if (this.probe) void this.probe.refresh();
  }

  /**
   * TEMPORARY, dev only: what the scan screen's pose source pill
   * (`src/Components/DevPoseSourcePill`) needs. Thin on purpose, like the rest
   * of this module: the wording, and the rules for when the pill turns red,
   * live in `describePoseSource.ts`, which is tested.
   */
  getDevStatus(): DevPoseStatus {
    const stats = this.client ? this.client.getStats() : null;
    // `peek`, never `current`: a read must not route (PoseSourceController).
    const decision = this.controller ? this.controller.peek() : null;
    let cloudUnavailableReason: string | null = null;
    if (!this.endpoint) cloudUnavailableReason = "no pose endpoint in this build";
    else if (!this.configLoaded) cloudUnavailableReason = "still reading remote config";
    else if (!this.config.enabled) {
      cloudUnavailableReason = "off in remote_config/pwa, or the read failed";
    }
    return {
      source: decision ? decision.source : "device",
      reason: decision ? decision.reason : "disabled",
      pinned: this.controller ? this.controller.isPinned() : false,
      cloudUnavailableReason,
      serverPoseRequests: stats ? stats.serverPoseRequests : 0,
      serverPoseFailures: stats ? stats.serverPoseFailures : 0,
      detectorBroken: detectorHealth.isBroken(),
    };
  }

  /** Rides the existing `scan.alignment.summary` / `scan.rotation.summary` rows. */
  getStats(): RemotePoseStats & {
    poseSource: string;
    poseSourceReason: string;
    /** Frozen for the rotation when the row was written. */
    poseSourcePinned: boolean;
    sessionDemoted: boolean;
    liveSwitchReason: string | null;
  } {
    const base: RemotePoseStats = this.client
      ? this.client.getStats()
      : {
          serverPoseRequests: 0,
          serverPoseFailures: 0,
          serverPoseDropped: 0,
          serverPoseRttP50Ms: null,
          serverPoseRttP90Ms: null,
        };
    // `peek`, never `current`: a summary row must not route either.
    const decision = this.controller ? this.controller.peek() : null;
    return {
      ...base,
      poseSource: decision ? decision.source : "device",
      // A routing decision nobody can explain is a decision nobody can roll back.
      poseSourceReason: decision ? decision.reason : "disabled",
      poseSourcePinned: this.controller ? this.controller.isPinned() : false,
      sessionDemoted: this.controller ? this.controller.isSessionDemoted() : false,
      liveSwitchReason: this.controller ? this.controller.getAttemptLiveReason() : null,
    };
  }
}

export const serverPoseService = new ServerPoseService();
export { encodeFrame, readEndpoint };
